import { DatePicker, Form, Input, InputNumber, Table, Tabs, Tooltip, message } from 'antd'
import axios from 'axios'
import { ArrowDownToLine, Building2 } from 'lucide-react'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { FaCog, FaDatabase, FaEdit, FaHeartbeat, FaList, FaPlus, FaShieldAlt, FaSyncAlt } from 'react-icons/fa'
import { RiDeleteBin6Line } from 'react-icons/ri'
import io from 'socket.io-client'
import { CustomModal, DeleteModal, SearchIpAddresses } from '../../Components'
import { secureApi } from '../../Config/api'
import { GET, POST } from '../../utils/apis'
import { translations } from '../../utils/constants'
import CustomFilterDropdown from '../../Components/CustomFilterDropdown/CustomFilterDropdown'
import { FilterOutlined } from '@ant-design/icons'
import { max } from 'lodash'

const { RangePicker } = DatePicker
const { TabPane } = Tabs

const Validator = ({ language }) => {
    const [validators, setValidators] = useState([])
    const [loading, setLoading] = useState(false)
    const [columnFilters, setColumnFilters] = useState({});
    const [filters, setFilters] = useState(null);
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)
    const [totalItems, setTotalItems] = useState(0)
    const [searchModal, setSearchModal] = useState(false)
    const [selectedIpAddresses, setSelectedIpAddresses] = useState([])
    const [dateRange, setDateRange] = useState([])
    const [isModalVisible, setIsModalVisible] = useState(false)
    const [editingRecord, setEditingRecord] = useState(null)
    const [deleteModal, setDeleteModal] = useState(false)
    const [deleteRecord, setDeleteRecord] = useState(null)
    const [allIpsAndPorts, setAllIpsAndPorts] = useState([])
    const [validatorIpData, setValidatorIpData] = useState([])
    const [ipCount, setIPCount] = useState(0)
    const [selectedDate, setSelectedDate] = useState(moment())
    const [sorter, setSorter] = useState({ sortBy: 'is_online', sortOrder: 'DESC' })
    const [dashboardCounts, setDashboardCounts] = useState({ totalIps: 0, totalTickets: 0, checkSsh: 0, ccEnabled: 0, tapOnfly: 0, turnstileActive: 0 })
    const [dashboardCards, setDashboardCards] = useState({ totalValidators: 0, checkSsh: 0, ccEnabled: 0, tapOnfly: 0 })
    const [form] = Form.useForm()
    useEffect(() => {
        fetchValidatorIP()
        fetchValidators()
    }, [currentPage, pageSize, selectedIpAddresses, dateRange, sorter, selectedDate])

    useEffect(() => {
        const socket = io()
        socket.on('validator', () => {
            fetchValidators()
            fetchValidatorIP()
        })
        return () => {
            socket.off('validator')
            socket.disconnect()
        }
    }, [])

    const fetchValidators = async () => {
        setLoading(true)
        try {
            const response = await secureApi.get(GET?.GET_VALIDATORS, {
                params: {
                    ip_addresses: selectedIpAddresses.length > 0 ? selectedIpAddresses.join(',') : undefined,
                    date: selectedDate ? selectedDate.format("YYYY-MM-DD") : undefined,
                }
            })
            if (response?.success) {

                // setValidators(response?.data)
                // setTotalItems(response?.total || 0)
                setDashboardCounts({
                    totalIps: response?.summary?.totalIps || 0,
                    totalTickets: response?.summary?.totalTickets || 0,
                    checkSsh: response?.summary?.checkSsh || 0,
                    ccEnabled: response?.summary?.ccEnabled || 0,
                    tapOnfly: response?.summary?.tapOnfly || 0,
                    turnstileActive: response?.summary?.turnstileActive || 0
                })
                setDashboardCards(prev => ({
                    ...prev,
                    checkSsh: response?.cards?.checkSsh || 0,
                    ccEnabled: response?.cards?.ccEnabled || 0,
                    tapOnfly: response?.cards?.tapOnfly || 0
                }))

            } else {
                message.error('Failed to fetch validators')
            }
        } catch {
            message.error('Error fetching validators')
        } finally {
            setLoading(false)
        }
    }

    const fetchValidatorIP = async (page = currentPage, limit = pageSize) => {
        setLoading(true)
        try {
            const response = await secureApi.get(GET?.GET_VALIDATORS_IP_AND_DETAILS, {
                params: {
                    page,
                    limit,
                    ip_addresses: selectedIpAddresses.length > 0 ? selectedIpAddresses.join(',') : undefined,
                    dateRange: dateRange?.length > 0 ? dateRange.map(d => d.format('YYYY-MM-DD')) : undefined,
                    sortBy: sorter?.sortBy,
                    sortOrder: sorter?.sortOrder,
                    date: selectedDate ? selectedDate.format("YYYY-MM-DD") : undefined,
                    port_number: columnFilters?.port_number,
                    company: columnFilters?.agency_id,
                    status: columnFilters?.status,
                    customer: columnFilters?.client,
                    enable_cc: columnFilters?.enable_cc,
                    ip_address: columnFilters?.ip_address,
                    terminal_id: columnFilters?.terminal_id,
                    registration_number: columnFilters?.registration_number,
                    use_money: columnFilters?.use_money,
                    max_ticket: columnFilters?.max_ticket,
                    tap_on_fly: columnFilters?.tap_on_fly,
                    check_ssh: columnFilters?.check_ssh,
                    plate_code: columnFilters?.plate_code,
                    firmware_version: columnFilters?.firmware_version,
                    updated_at: columnFilters?.updated_at,

                }

            })

            // console.log('Validator IP Response:', response)
            if (response?.success) {
                setValidatorIpData(response?.data)
                setTotalItems(response?.total || 0)
                setAllIpsAndPorts(response?.allIpsAndPorts || [])
                setDashboardCards(prev => ({
                    ...prev,
                    totalValidators: (response?.allIpsAndPorts || []).length
                }))
                setFilters(response?.filters);

            } else {
                message.error('Failed to fetch validator IP details')
            }
        } catch {
            message.error('Error fetching validator IP details')
        } finally {
            setLoading(false)
        }
    }

    const handleFormSubmit = async () => {
        try {
            const values = await form.validateFields()
            const payload = { ipAddress: values.ip_address, portNumber: values.port_number }
            if (editingRecord) {
                const res = await secureApi.post(POST.UPDATE_VALIDATOR, { id: editingRecord.id, ...payload })
                if (res?.success) {
                    message.success('IP updated successfully')
                    fetchValidators()
                    fetchValidatorIP(currentPage, pageSize)
                } else {
                    message.error(res?.error || res?.message || 'Update failed')
                }
            } else {
                const res = await secureApi.post(POST?.ADD_VALIDATOR, payload)
                if (res?.success) {
                    message.success('IP added successfully')
                    fetchValidators()
                    fetchValidatorIP(currentPage, pageSize)
                } else {
                    message.error(res?.error || res?.message || 'Add failed')
                }
            }
            setIsModalVisible(false)
            setEditingRecord(null)
            form.resetFields()
        } catch { }
    }

    const handleDelete = async () => {
        try {
            const res = await secureApi.post(POST?.DELETE_VALIDATOR, { id: deleteRecord?.id })
            if (res?.success) {
                message.success('Validator deleted successfully')
                fetchValidatorIP(currentPage, pageSize)
                fetchValidators()
            } else {
                message.error(res?.message || 'Delete failed')
            }
        } catch {
            message.error('Delete failed')
        } finally {
            setDeleteModal(false)
            setDeleteRecord(null)
        }
    }

    const refreshValidatorsFromAPI = async () => {
        if (loading) {
            return message.info(translations[language]?.pleaseWait || 'Please wait for the current process to finish.')
        }
        setLoading(true)
        try {
            message.loading({ content: translations[language]?.refreshValidatorsMessage || 'The validators list is being refreshed and may take a while.', key: 'refreshValidators', duration: 3 })
            const response = await secureApi.post(POST?.REFRESH_VALIDATOR_DATA)
            if (response?.success) {
                message.success(translations[language]?.refreshValidatorsSuccess || 'Validators List Refresh successfully')
                setLoading(false)
            }
            else {
                message.error(response?.message || translations[language]?.refreshValidatorsFailed || 'Failed to Refresh validators')
            }
        }
        catch (error) {
            console.error(error)
            message.error(translations[language]?.refreshValidatorsFailed || 'Something went wrong while Refresh validators')
        }
        finally {
            setLoading(false)
        }
    }

    const agencyMap = {
        10: "tuabruzzo",
        41: "atmmessina"
    }

    const columns = [
        {
            title: translations?.[language]?.ipAddress || "IP Address",
            dataIndex: "ip_address",
            key: "ip_address",
            filterDropdown: (props) => (
                <CustomFilterDropdown
                    language={language}
                    {...props}
                    data={filters?.ip_addresses}
                    placeholder={`${translations?.[language]?.search || 'Search'} IP...`}
                    noDataText={translations?.[language]?.noIpFound || "No Ip Found"}
                />
            ),
            filterIcon: <FilterOutlined />,
            render: (text) => text ? <span className='vl-plate'>{text}</span> : <span className='vl-muted'>-</span>
        },
        {
            title: translations?.[language]?.portNumber || "Port Number",
            dataIndex: "port_number",
            key: "port_number",
            filterDropdown: (props) => (
                <CustomFilterDropdown
                    language={language}
                    {...props}
                    data={filters?.ports}
                    placeholder={`${translations?.[language]?.search || 'Search'} Port...`}
                    noDataText={translations?.[language]?.noPortFound || "No Port Found"}
                />
            ),
            filterDropdownAlign: { points: ['tr', 'br'] },
            filterIcon: <FilterOutlined />,
            render: (text) => (text !== null && text !== undefined && text !== '') ? <span className='vl-plate'>{text}</span> : <span className='vl-muted'>-</span>
        },
        {
            title: translations?.[language]?.enableCc || "Enable CC",
            dataIndex: 'enable_cc',
            key: 'enable_cc',
            filterDropdown: (props) => (
                <CustomFilterDropdown
                    language={language}
                    {...props}
                    data={[
                        { label: translations?.[language]?.enabled || 'Enabled', value: 'Enabled' },
                        { label: translations?.[language]?.disabled || 'Disabled', value: 'Disabled' }
                    ]}
                    placeholder={`${translations?.[language]?.search || 'Search'} CC Status...`}
                />
            ),
            filterIcon: <FilterOutlined />,
            render: v =>
                v !== null && v !== undefined && v !== '-' ? (
                    <span className={`dash-status-pill ${v ? 'dash-status-ok' : 'dash-status-ko'}`}>
                        <span className='dash-status-dot' />
                        {v ? translations?.[language]?.enabled : translations?.[language]?.disabled}
                    </span>
                ) : <span className='vl-muted'>-</span>
        },
        {
            title: translations?.[language]?.status || "Status",
            dataIndex: "is_online",
            key: "is_online",
            sorter: true,
            filterDropdown: (props) => (
                <CustomFilterDropdown
                    language={language}
                    {...props}
                    data={[
                        { label: translations?.[language]?.online || 'Online', value: 'Online' },
                        { label: translations?.[language]?.offline || 'Offline', value: 'Offline' }
                    ]}
                    placeholder={`${translations?.[language]?.search || 'Search'} Status...`}
                />
            ),
            filterIcon: <FilterOutlined />,
            render: (is_online) => {
                if (is_online === true)
                    return <span className='dash-status-pill dash-status-ok'><span className='dash-status-dot' />{translations?.[language]?.online || "Online"}</span>;
                if (is_online === false)
                    return <span className='dash-status-pill dash-status-ko'><span className='dash-status-dot' />{translations?.[language]?.offline || "Offline"}</span>;
                return <span className='dash-status-pill dash-status-ko'><span className='dash-status-dot' />{translations?.[language]?.unknown || "Unknown"}</span>;
            }
        },
        {
            title: translations?.[language]?.regNumber || "Reg Number",
            dataIndex: 'registration_number',
            key: 'registration_number',
            filterDropdown: (props) => (
                <CustomFilterDropdown
                    language={language}
                    {...props}
                    data={filters?.registration_numbers}
                    placeholder={`${translations?.[language]?.search || 'Search'} Reg...`}
                    noDataText={translations?.[language]?.noRegFound || "No registration numbers Found"}
                />
            ),
            filterIcon: <FilterOutlined />,
            render: (reg, record) => (reg || record?.reg_number) ? (reg || record?.reg_number) : <span className='vl-muted'>-</span>
        },
        {
            title: translations?.[language]?.company || "Company",
            dataIndex: 'agency_id',
            key: 'agency_id',
            filterDropdown: (props) => (
                <CustomFilterDropdown
                    language={language}
                    {...props}
                    data={filters?.companies}
                    placeholder={`${translations?.[language]?.search || 'Search'} Company...`}
                    noDataText={translations?.[language]?.noCompanyFound || "No Company Found"}
                />
            ),
            filterDropdownAlign: { points: ['tr', 'br'] },
            filterIcon: <FilterOutlined />,
            render: (id) => (agencyMap[id] || id) ? (agencyMap[id] || id) : <span className='vl-muted'>-</span>
        },
        {
            title: translations?.[language]?.customerName || "Customer",
            dataIndex: 'client',
            key: 'client',
            filterDropdown: (props) => (
                <CustomFilterDropdown
                    language={language}
                    {...props}
                    data={filters?.customers}
                    placeholder={`${translations?.[language]?.search || 'Search'} Customer...`}
                    noDataText={translations?.[language]?.noCustomerFound || "No Customer Found"}
                />
            ),
            filterDropdownAlign: { points: ['tr', 'br'] },
            filterIcon: <FilterOutlined />,
            render: (client) => client ? <span>{client}</span> : <span className='vl-muted'>-</span>
        },
        {
            title: translations?.[language]?.terminalId || "Terminal ID",
            dataIndex: 'terminal_id',
            key: 'terminal_id',
            filterDropdown: (props) => (
                <CustomFilterDropdown
                    language={language}
                    {...props}
                    data={filters?.terminal_ids}
                    placeholder={`${translations?.[language]?.search || 'Search'} Terminal...`}
                    noDataText={translations?.[language]?.noTerminalFound || "No Terminal ID Found"}
                />
            ),
            filterIcon: <FilterOutlined />,
            render: (text) => text ? <span className='vl-plate'>{text}</span> : <span className='vl-muted'>-</span>
        },
        {
            title: translations?.[language]?.useMoney || "Use Money",
            dataIndex: 'use_money',
            key: 'use_money',
            filterDropdown: (props) => (
                <CustomFilterDropdown
                    language={language}
                    {...props}
                    data={[
                        { label: translations?.[language]?.enabled || 'Enabled', value: 'Enabled' },
                        { label: translations?.[language]?.disabled || 'Disabled', value: 'Disabled' }
                    ]}
                    placeholder={`${translations?.[language]?.search || 'Search'} Use Money...`}
                />
            ),
            filterIcon: <FilterOutlined />,
            render: v =>
                v !== null && v !== undefined && v !== '-' ? (
                    <span className={`dash-status-pill ${v ? 'dash-status-ok' : 'dash-status-ko'}`}>
                        <span className='dash-status-dot' />
                        {v ? translations?.[language]?.enabled : translations?.[language]?.disabled}
                    </span>
                ) : <span className='vl-muted'>-</span>
        },
        {
            title: translations?.[language]?.maxTicket || "Max Ticket",
            dataIndex: "max_ticket",
            key: "max_ticket",
            filterDropdown: (props) => (
                <CustomFilterDropdown
                    language={language}
                    {...props}
                    data={filters?.max_ticket}
                    placeholder={`${translations?.[language]?.search || 'Search'} Max...`}
                    noDataText={translations?.[language]?.noMaxTicketFound || "No Max Ticket Found"}
                />
            ),
            filterIcon: <FilterOutlined />,
        },
        {
            title: translations?.[language]?.tapOnFly || "Tap On Fly",
            dataIndex: "tap_on_fly",
            key: "tap_on_fly",
            filterDropdown: (props) => (
                <CustomFilterDropdown
                    language={language}
                    {...props}
                    data={[
                        { label: translations?.[language]?.enabled || 'Enabled', value: 'Enabled' },
                        { label: translations?.[language]?.disabled || 'Disabled', value: 'Disabled' }
                    ]}
                    placeholder={`${translations?.[language]?.search || 'Search'} Tap...`}
                />
            ),
            filterIcon: <FilterOutlined />,
            render: v =>
                v !== null && v !== undefined && v !== '-' ? (
                    <span className={`dash-status-pill ${v ? 'dash-status-ok' : 'dash-status-ko'}`}>
                        <span className='dash-status-dot' />
                        {v ? translations?.[language]?.enabled : translations?.[language]?.disabled}
                    </span>
                ) : <span className='vl-muted'>-</span>
        },
        {
            title: translations?.[language]?.checkSsh || "Check SSH",
            dataIndex: 'check_ssh',
            key: 'check_ssh',
            filterDropdown: (props) => (
                <CustomFilterDropdown
                    language={language}
                    {...props}
                    data={[
                        { label: translations?.[language]?.enabled || 'Enabled', value: 'Enabled' },
                        { label: translations?.[language]?.disabled || 'Disabled', value: 'Disabled' }
                    ]}
                    placeholder={`${translations?.[language]?.search || 'Search'} SSH...`}
                />
            ),
            filterIcon: <FilterOutlined />,
            render: v =>
                v !== null && v !== undefined ? (
                    <span className={`dash-status-pill ${v ? 'dash-status-ok' : 'dash-status-ko'}`}>
                        <span className='dash-status-dot' />
                        {v ? translations?.[language]?.enabled : translations?.[language]?.disabled}
                    </span>
                ) : <span className='vl-muted'>-</span>
        },
        {
            title: translations?.[language]?.namePlateCode || "Plate Code",
            dataIndex: 'plate_code',
            key: 'plate_code',
            filterDropdown: (props) => (
                <CustomFilterDropdown
                    language={language}
                    {...props}
                    data={filters?.plate_codes || []}
                    placeholder={`${translations?.[language]?.search || 'Search'} Plate...`}
                />
            ),
            filterIcon: <FilterOutlined />,
        },
        {
            title: translations?.[language]?.firmwareVersion || "Firmware",
            dataIndex: "firmware_version",
            key: "firmware_version",
            filterDropdown: (props) => (
                <CustomFilterDropdown
                    language={language}
                    {...props}
                    data={filters?.firmware_versions || []}
                    placeholder={`${translations?.[language]?.search || 'Search'} FW...`}
                />
            ),
            filterIcon: <FilterOutlined />,
            render: (v) => (v !== null && v !== undefined && v !== '') ? v : <span className='vl-muted'>-</span>
        },
        {
            title: translations?.[language]?.lastUpdated || "Last Updated",
            dataIndex: "updated_at",
            key: "updated_at",
            filterDropdown: (props) => (
                <CustomFilterDropdown
                    language={language}
                    {...props}
                    data={filters?.updated_at?.map(d => ({
                        label: moment(d).format("hh:mm a"),
                        value: d
                    }))}
                    placeholder={`${translations?.[language]?.search || 'Search'} Update...`}
                />
            ),
            filterIcon: <FilterOutlined />,
            render: (text) =>
                text ? (
                    <Tooltip title={moment(text).format("DD-MM-YYYY hh:mm a")}>
                        <span>{moment(text).format("hh:mm a")}</span>
                    </Tooltip>
                ) : <span className='vl-muted'>-</span>
        },
        {
            title: translations?.[language]?.action || 'Actions',
            key: 'actions',
            render: (_, record) => (
                <div className='vl-actions'>
                    <Tooltip title={translations?.[language]?.editValidator}>
                        <button
                            type='button'
                            className='vl-action-btn vl-action-edit'
                            onClick={() => {
                                setEditingRecord(record)
                                form.setFieldsValue({
                                    ip_address: record.ip_address,
                                    port_number: record.port_number
                                })
                                setIsModalVisible(true)
                            }}
                        >
                            <FaEdit size={14} />
                        </button>
                    </Tooltip>
                    <Tooltip title={translations?.[language]?.deleteValidator}>
                        <button
                            type='button'
                            className='vl-action-btn vl-action-delete'
                            onClick={() => {
                                setDeleteRecord(record)
                                setDeleteModal(true)
                            }}
                        >
                            <RiDeleteBin6Line size={15} />
                        </button>
                    </Tooltip>
                </div>
            )
        }
    ]

    const manageColumns = [
        {
            title: translations?.[language]?.ipAddress,
            dataIndex: 'ip_address',
            key: 'ip_address',
            render: (text) => text ? <span className='vl-plate'>{text}</span> : <span className='vl-muted'>-</span>
        },
        {
            title: translations?.[language]?.portNumber,
            dataIndex: 'port_number',
            key: 'port_number',
            render: (text) => text ? <span className='vl-plate'>{text}</span> : <span className='vl-muted'>-</span>
        },
        {
            title: translations?.[language]?.action || 'Actions',
            key: 'actions',
            render: (_, record) => (
                <div className='vl-actions'>
                    <Tooltip title={translations?.[language]?.editValidator}>
                        <button
                            type='button'
                            className='vl-action-btn vl-action-edit'
                            onClick={() => {
                                setEditingRecord(record)
                                form.setFieldsValue({
                                    ip_address: record.ip_address,
                                    port_number: record.port_number
                                })
                                setIsModalVisible(true)
                            }}
                        >
                            <FaEdit size={14} />
                        </button>
                    </Tooltip>
                    <Tooltip title={translations?.[language]?.deleteValidator}>
                        <button
                            type='button'
                            className='vl-action-btn vl-action-delete'
                            onClick={() => {
                                setDeleteRecord(record)
                                setDeleteModal(true)
                            }}
                        >
                            <RiDeleteBin6Line size={15} />
                        </button>
                    </Tooltip>
                </div>
            )
        }
    ]

    const cardsData = [
        { count: dashboardCards?.totalValidators, title: translations?.[language]?.totalValidators, bg: '#40AFFF', icon: <FaList size={22} color="white" /> },
        { count: dashboardCards?.checkSsh, title: translations?.[language]?.checkSsh, bg: '#00E096', icon: <FaHeartbeat size={22} color="white" /> },
        { count: dashboardCards?.ccEnabled, title: translations?.[language]?.enableCc, bg: '#1976D2', icon: <FaShieldAlt size={22} color="white" /> },
        { count: dashboardCards?.tapOnfly, title: translations?.[language]?.tapOnFly, bg: '#FF9800', icon: <FaDatabase size={22} color="white" /> }
    ]
    const getValidatorPdf = async () => {
        try {
            const payload = {
                ip_addresses: selectedIpAddresses.length > 0 ? selectedIpAddresses.join(',') : undefined,
                dateRange: dateRange?.length > 0 ? dateRange.map(d => d.format('YYYY-MM-DD')) : undefined,
                date: selectedDate ? selectedDate.format("YYYY-MM-DD") : undefined,
                language
            };

            const response = await axios.post(POST?.DOWNLOAD_VALIDATOR_PDF, payload, { responseType: 'blob' });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'validators_report.pdf');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            message.success(translations?.[language]?.pdfDownloadSuccess)
        } catch (err) {
            message.error(translations?.[language]?.pdfDownloadFail);
        }
    };


    const isValidIPv4 = (ip) => {
        if (!ip) return false;
        const parts = ip.split(".");
        if (parts.length !== 4) return false;

        return parts.every(part => {
            if (part === "" || isNaN(part)) return false;
            const num = Number(part);
            return num >= 0 && num <= 255 && String(num) === part.trim();
        });
    }

    return (
        <div className="main-container">
            <div className="main-inner-container">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                    <h1 className="main-heading">{translations?.[language]?.validator}</h1>
                    <div className="ip-address-filter-div">
                        <DatePicker
                            format="YYYY-MM-DD"
                            style={{ height: 40, borderRadius: 6 }}
                            value={selectedDate}
                            disabledDate={current => current && current > moment().endOf('day')}
                            onChange={(date) => {
                                if (date) {
                                    setSelectedDate(date)
                                } else {
                                    setSelectedDate(moment())
                                }
                            }}
                        />
                        <span
                            type="primary"
                            className={`organization search-ip-address ${searchModal ? 'open-organization' : 'close-organization'}`}
                            onClick={() => setSearchModal(!searchModal)}
                        >
                            <Building2 />
                            {translations[language]?.searchIpAddresses}
                            {ipCount ?
                                <div className="filter-count-badge">
                                    {ipCount}
                                </div>
                                : null
                            }
                        </span>
                    </div>
                </div>

                <div className='vd-stats' style={{ marginTop: 20 }}>
                    {cardsData?.map((card, idx) => (
                        <div key={idx} className='vd-stat'>
                            <div className='vd-stat-icon' style={{ backgroundColor: card.bg }}>
                                {card?.icon}
                            </div>
                            <div>
                                <p className='vd-stat-value'>{card?.count}</p>
                                <p className='vd-stat-label'>{card?.title}</p>
                            </div>
                        </div>
                    ))}
                </div>

                <div className='vd-card' style={{ marginTop: 20 }}>
                    <div className='vd-card-header'>
                        <div className='vl-list-title'>
                            <h2 className='vd-card-title'>{translations[language]?.validatorList}</h2>
                            <span className='vl-total-chip'>{translations[language]?.totalRecord}: <strong>{totalItems}</strong></span>
                        </div>

                        <div style={{ display: 'flex', gap: '10px' }} className='validator-header-buttons'>
                            <button
                                className="add-button validator-export-btn"
                                onClick={getValidatorPdf}
                            >
                                <ArrowDownToLine className="icon" />
                                <span>{translations?.[language]?.exportPdf}</span>
                            </button>
                            <button
                                className="add-button validator-export-btn"
                                onClick={() => {
                                    setEditingRecord(null)
                                    form.resetFields()
                                    setIsModalVisible(true)
                                }}
                                disabled={selectedDate && !selectedDate.isSame(moment(), 'day')}
                                style={{
                                    opacity: selectedDate && !selectedDate.isSame(moment(), 'day') ? 0.5 : 1,
                                    cursor: selectedDate && !selectedDate.isSame(moment(), 'day') ? "not-allowed" : "pointer"
                                }}
                            >
                                <FaPlus className="icon" />
                                <span>{translations?.[language]?.addValidator}</span>
                            </button>

                            <button
                                className="add-button validator-export-btn"
                                onClick={refreshValidatorsFromAPI}
                                disabled={selectedDate && !selectedDate.isSame(moment(), 'day')}
                                style={{
                                    opacity: selectedDate && !selectedDate.isSame(moment(), 'day') ? 0.5 : 1,
                                    cursor: selectedDate && !selectedDate.isSame(moment(), 'day') ? "not-allowed !important" : "pointer"
                                }}
                            >
                                <FaSyncAlt className="icon" />
                                <span>{translations?.[language]?.refreshButton}</span>
                            </button>
                        </div>
                    </div>

                    <div className='vd-card-body'>
                        <Table
                            // dataSource={validators}
                            dataSource={validatorIpData}
                            columns={columns}
                            className='vl-table'
                            pagination={{
                                current: currentPage,
                                pageSize: pageSize,
                                total: totalItems,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                onChange: (page, size) => {
                                    setCurrentPage(page)
                                    setPageSize(size)
                                }
                            }}
                            rowKey="id"
                            scroll={{ x: 'max-content' }}
                            onChange={(pagination, filters, sorterObj) => {

                                const mappedFilters = { ...filters };

                                if (filters.is_online) {
                                    mappedFilters.status = filters.is_online.filter(v =>
                                        v === "Online" || v === "Offline"
                                    );

                                }

                                delete mappedFilters.is_online;

                                Object.keys(mappedFilters).forEach(key => {
                                    if (Array.isArray(mappedFilters[key])) {
                                        mappedFilters[key] = mappedFilters[key].filter(v => v !== "" && v !== null && v !== undefined);
                                        if (mappedFilters[key].length === 0) delete mappedFilters[key];
                                    }
                                });

                                setColumnFilters(mappedFilters);
                                setCurrentPage(1);

                                // Sorting
                                if (sorterObj.order) {
                                    setSorter({
                                        sortBy: sorterObj.field,
                                        sortOrder: sorterObj.order === "ascend" ? "ASC" : "DESC",
                                    });
                                } else {
                                    setSorter({ sortBy: "is_online", sortOrder: "DESC" });
                                }
                            }}
                        />
                    </div>
                </div>

                {/* <Tabs defaultActiveKey="1" className="validator-tabs" style={{ marginTop: 20 }}>
                    <TabPane
                        key="1"
                        tab={
                            <span>
                                <FaDatabase style={{ marginRight: 6 }} />
                                {translations[language]?.validatorLog}
                            </span>
                        }
                    >
                        <div className="validator-table-header">
                            <h1 className="h1-theme-color">{translations[language]?.validatorList}</h1>

                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    className="add-button validator-export-btn"
                                    onClick={getValidatorPdf}
                                >
                                    <ArrowDownToLine className="icon" />
                                    <span>{translations?.[language]?.exportPdf}</span>
                                </button>

                                <button
                                    className="add-button validator-export-btn"
                                    onClick={refreshValidatorsFromAPI}
                                >
                                    <FaSyncAlt className="icon" />
                                    <span>Refresh</span>
                                </button>
                            </div>
                        </div>

                        <Table
                            dataSource={validators}
                            columns={columns}
                            pagination={{
                                current: currentPage,
                                pageSize: pageSize,
                                total: totalItems,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                onChange: (page, size) => {
                                    setCurrentPage(page)
                                    setPageSize(size)
                                }
                            }}
                            rowKey="id"
                            loading={loading}
                            scroll={{ x: 'max-content' }}
                            onChange={(pagination, filters, sorterObj) => {
                                if (sorterObj.order) {
                                    setSorter({
                                        sortBy: sorterObj.field,
                                        sortOrder: sorterObj.order === 'ascend' ? 'ASC' : 'DESC'
                                    })
                                } else {
                                    setSorter({ sortBy: 'created_at', sortOrder: 'DESC' })
                                }
                            }}
                            summary={() => (
                                <Table.Summary.Row style={{ borderTop: '2px solid #e8e8e8', backgroundColor: '#f5f7fa' }}>
                                    <Table.Summary.Cell index={0} colSpan={columns.length}>
                                        <b>{translations[language]?.totalRecord}: {totalItems}</b>
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            )}
                        />
                    </TabPane>

                    <TabPane
                        key="2"
                        tab={
                            <span>
                                <FaCog style={{ marginRight: 6 }} />
                                {translations[language]?.validatorManagement}
                            </span>
                        }
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
                            <h1 className="h1-theme-color">{translations?.[language]?.manageValidators}</h1>
                            <div className='validator-btn-div'>
                                <button
                                    className="add-button"
                                    onClick={getValidatorIPPdf}
                                    style={{ marginRight: 10 }}
                                >
                                    <ArrowDownToLine className="icon" />
                                    <span>{translations?.[language]?.exportPdf}</span>
                                </button>
                                <button
                                    className="add-button"
                                    onClick={() => {
                                        setEditingRecord(null)
                                        form.resetFields()
                                        setIsModalVisible(true)
                                    }}
                                >
                                    <FaPlus className="icon" />
                                    <span>{translations?.[language]?.addValidator}</span>
                                </button>
                            </div>
                        </div>

                        <Table
                            dataSource={validatorIpData}
                            columns={manageColumns}
                            rowKey="id"
                            loading={loading}
                            pagination={{
                                current: ipCurrentPage,
                                pageSize: ipPageSize,
                                total: ipTotalItems,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                onChange: (page, size) => {
                                    setIpCurrentPage(page)
                                    setIpPageSize(size)
                                }
                            }}
                            scroll={{ x: 'max-content' }}
                        />
                    </TabPane>
                </Tabs> */}
            </div>

            <CustomModal
                isVisible={isModalVisible}
                onClose={() => {
                    setIsModalVisible(false)
                    setEditingRecord(null)
                    form.resetFields()
                }}
                title={editingRecord ? translations[language]?.editValidator : translations[language]?.addValidator}
                actionText={editingRecord ? translations[language]?.update : translations?.[language]?.addValidator}
                onAction={handleFormSubmit}
                language={language}
            >
                <Form layout="vertical" form={form}>
                    <Form.Item
                        label={translations[language]?.ipAddress || 'IP Address'}
                        name="ip_address"
                        rules={[
                            { required: true, message: translations[language]?.pleaseEnterIpAddress || 'Please enter IP address' },
                            {
                                validator: (_, value) =>
                                    !value || isValidIPv4(value)
                                        ? Promise.resolve()
                                        : Promise.reject(new Error(translations[language]?.invalidIPv4 || 'Invalid IPv4 format (e.g. 192.168.0.1)')),
                            },
                        ]}
                    >
                        <Input placeholder={translations[language]?.pleaseEnterIpAddress || 'Please enter IP address'} style={{ height: 40, borderRadius: 6 }} />
                    </Form.Item>
                    <Form.Item
                        label={translations[language]?.portNumber}
                        name="port_number"
                    >
                        <InputNumber parser={(value) => Math.abs(Number(value || 0))} min={0} placeholder={translations[language]?.portNumber} type="number" style={{ height: 40, borderRadius: 6, width: '100%' }} />
                    </Form.Item>
                </Form>
            </CustomModal>

            <DeleteModal
                title={translations[language]?.deleteValidator}
                bodyText={<>{translations[language]?.areYouSureYouWantToDltThisValidator}</>}
                isOpen={deleteModal}
                onCancel={() => setDeleteModal(false)}
                onDelete={handleDelete}
                language={language}
            />

            <div className={`search-organization-main-div ${searchModal ? 'open' : ''}`}>
                <SearchIpAddresses
                    selectedIpAddresses={selectedIpAddresses}
                    handleIpAddressChange={ips => {
                        setSelectedIpAddresses(ips)
                        setCurrentPage(1)
                    }}
                    ipAddresses={allIpsAndPorts}
                    setSearchModal={setSearchModal}
                    searchModal={searchModal}
                    language={language}
                    setIPCount={setIPCount}
                />
            </div>
        </div>
    )
}

export default Validator